// AFPES R3 UAT follow-up: quota display and pre-questionnaire admission gate.
// Server-side finalization remains authoritative. No local usage counting.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEntitlement from "../hooks/useEntitlement";
import { supabase } from "../lib/supabaseClient";

const LIMITED = new Set(["pro", "enterprise"]);
const PLAN_ALLOWANCE = { pro: 2, enterprise: 10 };

async function readAllowance(expectedPlan) {
  // Bound the entire read, including session verification. Never allow a
  // stalled auth lookup to leave the subscriber staring at an endless spinner.
  let timeoutId;
  const work = async () => {
    const { data: identity, error: authError } = await supabase.auth.getUser();
    if (authError || !identity?.user) throw new Error("Subscriber authentication is unavailable.");
    return supabase.functions.invoke("paid_assessment_finalize?offset=0", { method: "GET" });
  };
  let response;
  try {
    response = await Promise.race([
      work(),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("Allowance verification timed out. Please retry.")), 15000);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
  const { data, error } = response;
  if (error || !data || !Array.isArray(data.snapshots)) {
    throw new Error("We cannot verify your assessment allowance right now.");
  }
  const usage = data.usage;
  if (!usage || usage.plan_key !== expectedPlan ||
      usage.allowed !== PLAN_ALLOWANCE[expectedPlan] ||
      !Number.isSafeInteger(usage.used) || usage.used < 0 ||
      typeof usage.next_reset !== "string" || !Number.isFinite(Date.parse(usage.next_reset))) {
    throw new Error("Your assessment allowance cannot currently be confirmed.");
  }
  return usage;
}

function usePaidAllowance() {
  const entitlement = useEntitlement();
  const plan = String(entitlement.rawPlanKey || "").toLowerCase();
  const limited = LIMITED.has(plan);
  const [state, setState] = useState({ status: "loading", usage: null, error: "" });
  useEffect(() => {
    let active = true;
    setState({ status: "loading", usage: null, error: "" });
    if (entitlement.loading) return () => { active = false; };
    if (entitlement.error) {
      setState({ status: "error", usage: null, error: "Subscription verification is unavailable." });
      return () => { active = false; };
    }
    if (!limited) {
      setState({ status: ["global_pro", "precision_pass"].includes(plan) ? "unlimited" : "error", usage: null, error: "Subscription plan could not be verified." });
      return () => { active = false; };
    }
    readAllowance(plan).then((usage) => {
      if (active) setState({ status: "ready", usage, error: "" });
    }).catch((error) => {
      if (active) setState({ status: "error", usage: null, error: error.message });
    });
    return () => { active = false; };
  }, [entitlement.loading, entitlement.error, plan, limited]);
  return { ...state, limited, plan, resolving: Boolean(entitlement.loading) };
}

function AllowanceMessage({ value, blocking = false }) {
  const navigate = useNavigate();
  const { status, usage, error, plan, resolving } = value;
  if (status === "loading" || resolving) {
    return <div role="status" className="rounded-2xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-700">Checking your assessment allowance securely…</div>;
  }
  const exhausted = status === "ready" && usage.used >= usage.allowed;
  const renewal = status === "ready" ? new Date(usage.next_reset).toLocaleDateString() : null;
  const title = exhausted ? "Monthly Assessment Allowance Reached" : "Assessment Allowance Temporarily Unavailable";
  return <div role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-slate-900 shadow-sm">
    <h2 className="text-xl font-extrabold">{title}</h2>
    <p className="mt-2 text-sm leading-6">{exhausted
      ? `You've completed all ${usage.allowed} assessments included in your ${plan === "pro" ? "Pro" : "Enterprise"} subscription for the current billing period. Your allowance renews on ${renewal}.`
      : (error || "We couldn't verify your subscription allowance. No new assessment will start until verification succeeds.")}</p>
    <p className="mt-2 text-sm">Your completed reports and remediation tools remain available.</p>
    <div className="mt-4 flex flex-wrap gap-2">
      <button type="button" onClick={() => navigate("/reports")} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white">View Existing Reports</button>
      <button type="button" onClick={() => navigate("/dashboard")} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold">Return to Dashboard</button>
      {!exhausted ? <button type="button" onClick={() => window.location.reload()} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold">Retry Verification</button> : null}
    </div>
  </div>;
}

export function PaidAssessmentEntryGate({ children }) {
  const allowance = usePaidAllowance();
  if (!allowance.limited && !allowance.resolving && allowance.status === "unlimited") return children;
  if (allowance.status === "ready" && allowance.usage.used < allowance.usage.allowed) return children;
  return <div className="min-h-screen bg-slate-50 p-8 md:p-12"><div className="mx-auto max-w-3xl"><AllowanceMessage value={allowance} blocking /></div></div>;
}

export function PaidAssessmentAllowancePanel() {
  const allowance = usePaidAllowance();
  if (!allowance.limited) return null;
  if (allowance.status !== "ready") {
    return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-label="Monthly assessment allowance">
      <h2 className="font-black text-slate-900">Monthly Assessment Allowance</h2>
      <p className="mt-2 text-sm text-slate-600">{allowance.status === "loading" ? "Checking current usage…" : "Current usage could not be verified. Your existing reports remain available."}</p>
    </section>;
  }
  const { used, allowed, next_reset } = allowance.usage;
  const remaining = Math.max(0, allowed - used);
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-label="Monthly assessment allowance">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-black text-slate-900">Monthly Assessment Allowance</h2>
      <span className="text-xs font-extrabold uppercase tracking-wider text-slate-500">{allowance.plan === "pro" ? "Pro Plan" : "Enterprise"}</span>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3 text-center">
      {[{ label: "Completed", count: used }, { label: "Remaining", count: remaining }, { label: "Monthly allowance", count: allowed }].map((item) =>
        <div key={item.label} className="rounded-xl bg-slate-50 p-3"><div className="text-2xl font-black text-slate-900">{item.count}</div><div className="text-xs text-slate-600">{item.label}</div></div>)}
    </div>
    <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={Math.min(used,allowed)} aria-valuemin={0} aria-valuemax={allowed} aria-label="Assessment allowance used">
      <div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.min(100, used * 100 / allowed)}%` }} />
    </div>
    <p className="mt-3 text-sm text-slate-600">{used} of {allowed} assessments used. Next allowance period: {new Date(next_reset).toLocaleDateString()}.</p>
  </section>;
}
